import { ColumnDef } from "@tanstack/react-table";
import { CardConditionEnum, FinishEnum } from "modules/cards/model/card";
import ConditionChip from "modules/common/components/Table/ConditionChip";
import LanguageFlag from "modules/common/components/Table/LanguageFlag";
import SetTooltip from "modules/common/components/Table/SetTooltip";
import { TradeItem, TradeListType } from "modules/tradeLists/model/tradeList";
import { useIsUserPage } from "modules/users/hooks/useIsUserPage";
import { RelativeTime } from "modules/common/components/ui/RelativeTime";
import React, { useMemo } from "react";
import RecentTradesContactButton from "modules/tradeLists/components/RecentTrades/RecentTradesContactButton";
import { TradeItemListingLink } from "modules/tradeLists/components/RecentTrades/TradeItemListingLink";
import { UserHoverCard } from "modules/common/components/navigation/UserHoverCard";
import { cn, getCardPrice } from "utils/utils";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import { getTradeRelationship } from "modules/tradeLists/utils/tradeRelationshipUtils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { FetchCardDataNameCell } from "modules/collections/details/components/FetchCardDataNameCell";
import { useCardDetailsModal } from "modules/cards/hooks/useCardDetailsModal";
import { SelectionCheckbox } from "modules/common/components/bulk-actions/SelectionCheckbox";
import { SelectionVisibilityWrapper } from "modules/common/components/bulk-actions/SelectionVisibilityWrapper";
import { ContactButtonCell } from "modules/common/components/bulk-actions/ContactButtonCell";
import { ActiveProposalBadge } from "modules/tradeLists/components/ActiveProposalBadge";
import { availableQuantity } from "modules/tradeLists/utils/tradeItemAvailability";
import { primaryDisplayFinish } from "modules/cards/model/finishBehavior";
import { CardFinishIndicator } from "modules/common/components/CardSpecOverlay";

interface UseTradeColumnsProps {
  showRelationship?: boolean;
  showUpdated?: boolean;
  alwaysShowUser?: boolean;
  isViewingUserPage?: boolean;
  contactButtonSize?: "small" | "large";
  enableMultiSelect?: boolean;
}

export function useTradeColumns({
  showRelationship = false,
  showUpdated = true,
  alwaysShowUser = false,
  isViewingUserPage = false,
  contactButtonSize = "small",
  enableMultiSelect = false,
}: UseTradeColumnsProps = {}): ColumnDef<TradeItem>[] {
  const { userId, isPending: isPendingUser } = useAppUser();
  const isUserPage = useIsUserPage();
  const { handleOpenCardDetailsByDataId } = useCardDetailsModal();

  return useMemo(() => {
    const baseColumns: ColumnDef<TradeItem>[] = [
      {
        accessorKey: "quantity",
        size: 24,
        header: () => <div className="text-center">Qty</div>,
        cell: (info) => (
          <div className="text-base-content/70 text-center text-sm">
            {info.getValue() as number}
          </div>
        ),
      },
      {
        accessorKey: "name",
        header: "Card",
        cell: (info) => {
          const item = info.row.original;
          const finish = primaryDisplayFinish(item.finishes);
          return (
            <div className={"flex flex-row gap-1"}>
              <CardFinishIndicator finish={finish} />
              <FetchCardDataNameCell
                onClick={() =>
                  handleOpenCardDetailsByDataId(item.dataId, item.game, finish)
                }
                basicCard={item}
                game={item.game}
                name={info.getValue() as string}
              />
              <ActiveProposalBadge item={item} className="shrink-0" />
            </div>
          );
        },
      },
      ...(showRelationship
        ? [
            {
              id: "relationship",
              header: "Type",
              cell: (info: any) => {
                const item = info.row.original;
                const relationship = getTradeRelationship(item, userId || "");
                const RelationshipIcon = relationship.icon;

                return (
                  <div
                    className={cn(
                      "tooltip tooltip-top text-base-100",
                      relationship.type === "they_have"
                        ? "tooltip-warning"
                        : "tooltip-secondary"
                    )}
                    data-tip={relationship.text}
                  >
                    <div
                      className={cn(
                        "rounded-selector flex w-fit items-center justify-center gap-2 p-2",
                        relationship.bgColor
                      )}
                    >
                      <RelationshipIcon
                        size={ICON_SIZE}
                        className={cn(relationship.color)}
                      />
                    </div>
                    {/*<PortalTooltip
                    content={
                      <div
                        className={cn(
                          "text-base-100 mb-1 rounded-sm px-2 py-1 text-sm",
                          relationship.type === "they_have"
                            ? "bg-warning"
                            : "bg-secondary"
                        )}
                      >
                        {relationship.text}
                      </div>
                    }
                    placement="top"
                  >
                    <RelationshipIcon
                      size={ICON_SIZE}
                      className={cn(relationship.color)}
                    />
                  </PortalTooltip>*/}
                  </div>
                );
              },
            },
          ]
        : []),

      {
        accessorKey: "setCode",
        size: 60,
        header: () => <div className="text-center">Set</div>,
        cell: (info) => (
          <div className="flex items-center justify-center">
            <SetTooltip item={info.row.original} />
          </div>
        ),
      },
      {
        id: "price",
        size: 80,
        header: () => <div className="text-center">Price</div>,
        cell: (info) => {
          const item = info.row.original;
          const price = getCardPrice(item);
          return (
            <div className="text-base-content text-center text-sm">
              {price || "—"}
            </div>
          );
        },
      },
      {
        accessorKey: "language",
        size: 60,
        header: () => <div className="text-center">Lang</div>,
        cell: (info) => (
          <div className="flex justify-center">
            <LanguageFlag language={info.getValue() as string} />
          </div>
        ),
      },
      {
        accessorKey: "condition",
        size: 70,
        header: () => <div className="text-center">Cond</div>,
        cell: (info) => (
          <div className="flex justify-center">
            <ConditionChip
              condition={info.getValue() as CardConditionEnum}
              showMinimum={info.row.original.list?.type === TradeListType.WANTS}
            />
          </div>
        ),
      },
      ...(showUpdated
        ? [
            {
              accessorKey: "updatedAt",
              size: 130,
              header: "Updated",
              cell: (info: any) => (
                <RelativeTime
                  date={info.getValue() as Date}
                  className="block text-xs md:text-sm"
                />
              ),
            },
          ]
        : []),
    ];

    // Show user avatar column when:
    // 1. alwaysShowUser is true, OR
    // 2. NOT on a user page AND NOT explicitly viewing a user page AND (user is not logged in OR auth has loaded)
    const shouldShowUserColumn =
      alwaysShowUser ||
      (!isUserPage && !isViewingUserPage && (!userId || !isPendingUser));

    // Show contact/actions column when:
    // 1. alwaysShowUser is true, OR
    // 2. NOT on a user page AND (user is not logged in OR auth has loaded)
    const shouldShowContactColumn =
      alwaysShowUser || (!isUserPage && (!userId || !isPendingUser));

    if (shouldShowUserColumn) {
      baseColumns.push({
        id: "user",
        header: () => <div className="text-center">User</div>,
        cell: (info) => {
          const item = info.row.original;
          if (!item.user) {
            return (
              <div className="text-base-content/70 text-center text-sm">—</div>
            );
          }
          return (
            <div className="flex items-center justify-center gap-2">
              <UserHoverCard
                size="xs"
                userId={item.user.id}
                username={item.user.username}
                avatar={item.user.preferredAvatar}
                maxUsernameLength={12}
                usernameClassName="text-sm font-medium"
                activityStatus={item.activityStatus}
              />
            </div>
          );
        },
      });
    }

    if (shouldShowContactColumn) {
      baseColumns.push({
        id: "actions",
        size: 80,
        header: () => (
          <div className="text-center">
            {showRelationship ? "Contact" : "Contact"}
            {/*{showRelationship ? "Actions" : "Contact"}*/}
          </div>
        ),
        cell: (info) => {
          const item = info.row.original;
          if (!item.user) {
            return (
              <div className="text-base-content/70 text-center text-sm">—</div>
            );
          }
          const isCurrentUserItem = item.user.id === userId;

          if (isCurrentUserItem) {
            return showRelationship ? (
              <div className="text-base-content/60 text-center text-xs">
                Your item
              </div>
            ) : (
              <div className="text-center">
                <TradeItemListingLink item={item} size={"small"} />
              </div>
            );
          }

          return (
            <div
              className={
                showRelationship ? "flex justify-center" : "text-center"
              }
            >
              {enableMultiSelect ? (
                <ContactButtonCell
                  item={item}
                  size={contactButtonSize}
                  fromMatch={showRelationship}
                />
              ) : (
                <RecentTradesContactButton
                  item={item}
                  size={contactButtonSize}
                  fromMatch={showRelationship}
                />
              )}
            </div>
          );
        },
      });
    }

    if (enableMultiSelect) {
      baseColumns.unshift({
        id: "select",
        size: 24,
        header: () => null,
        cell: (info) => {
          // Nothing left to propose on — the ActiveProposalBadge in the name
          // column carries the explanation.
          const unavailable = availableQuantity(info.row.original) <= 0;
          return (
            <SelectionVisibilityWrapper>
              <SelectionCheckbox
                ids={info.row.original.ids}
                className="border-0 p-0"
                disabled={unavailable}
                disabledLabel="All copies are already in an active trade"
              />
            </SelectionVisibilityWrapper>
          );
        },
      });
    }

    return baseColumns;
  }, [
    handleOpenCardDetailsByDataId,
    userId,
    isPendingUser,
    isUserPage,
    showRelationship,
    showUpdated,
    alwaysShowUser,
    isViewingUserPage,
    contactButtonSize,
    enableMultiSelect,
  ]);
}
